import type MediaViewerService from './media-viewer.service';
import type {IDocumentService, IPromise, IQService, IScope, ITimeoutService, IWindowService} from 'angular';
import type WalkerService from '../commons/services/walker.service';
import {getUrlParams} from 'shared/utils/url-utils';
import {strToBool} from 'shared/utils/string-utils';
import type CurrentAuthService from '../commons/services/current-auth.service';
import type {Directory, RawBodyMailSendRequest, ViewableFile} from 'typings/portaro.be.types';
import {SenderDescriptorType} from 'typings/portaro.be.types';
import {identifiedValuesEquals} from 'shared/utils/custom-utils';
import type SearchApi from './search-api';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type ToasterService from '../commons/services/toaster-service';
import type {Searcher, SearcherFactory} from './searcher.factory';
import {FileViewForm} from 'typings/file-view-form';
import {KpVideoViewerEvent} from '../commons/components/kp-video-viewer/kp-video-viewer-event-wrapper.component';
import {KpVideoViewerAspectRatio} from '../commons/components/kp-video-viewer/kp-video-viewer.component';
import type MailerService from '../commons/services/mailer.service';
import type {LocalizationService} from '../commons/providers/localization.provider';
import type {SearchService} from '../commons/services/search.service';
import {Kind} from '../commons/constants/portaro.constants';
import type {UrlStateManagerService} from '../commons/services/url-state-manager.service';
import Checkbox from '../commons/ui-components/checkbox/Checkbox.svelte';

declare global {
    interface Window {
        model: any & {
            directoryTree: any;
        }
    }
}

const LOADING_FILE_TIMEOUT_MILLIS = 10000;

export default class MediaViewerController {
    public static controllerName = 'MediaViewerCtrl';

    public checkboxComponent = Checkbox;

    public searchInDirectoriesVisible = false;
    public searchInFileVisible = false;
    public copyLinkToClipboardVisible = false;
    public pageNumberToLink = false;
    public mediaViewerViewerSearchApi: SearchApi;
    public mediaViewerSearchInFileSearchApi: SearchApi;
    public searchInFileQuery: string;
    public mediaViewerCurrentPage: number;
    public mediaViewerPageCount: number;
    public videoPlayerAspectRatio: KpVideoViewerAspectRatio = KpVideoViewerAspectRatio.A16_9;

    private editMode: boolean;
    private backUrl: string;
    private searcher: Searcher<any>;
    private readonly viewer: MediaViewerService<ViewableFile>;

    /*@ngInject*/
    constructor(private $scope: IScope,
                private CONTEXT_PATH: string,
                private SERVER_URL: string,
                private searchService: SearchService,
                private walker: WalkerService,
                private $window: IWindowService,
                private $document: IDocumentService,
                private currentAuthService: CurrentAuthService,
                private mediaViewerService: MediaViewerService<ViewableFile>,
                private $timeout: ITimeoutService,
                private $q: IQService,
                private urlStateManagerService: UrlStateManagerService,
                private toasterService: ToasterService,
                private searcherFactory: SearcherFactory,
                private mailerService: MailerService,
                private localizationService: LocalizationService) {

        this.viewer = this.mediaViewerService;

        this.init();
    }

    public init() {
        this.viewer.setDirectoryTree(this.$window.model.directoryTree);

        const urlParams = getUrlParams(true);
        this.urlStateManagerService.register(this.onStateChange.bind(this));

        try {
            this.editMode = strToBool(urlParams.edit);
        } catch (e) {
            this.editMode = false;
        }
        this.backUrl = urlParams.origin || this.CONTEXT_PATH || '/';

        this.$window.onkeydown = (evt) => {
            switch (evt.keyCode) {
                case 37:
                case 38:
                    this.$scope.$apply(this.viewer.previous.bind(this.viewer));
                    break;
                case 39:
                case 40:
                    this.$scope.$apply(this.viewer.next.bind(this.viewer));
                    break;
            }
        };

        this.searcher = this.searcherFactory.create(
            async (query) => {
                const params = {
                    kind: Kind.KIND_FILE,
                    q: query,
                    pageSize: 50,
                    rootDirectory: urlParams.rootDirectory || this.viewer.thisCurrentDirectory.id
                };
                const searchResult = await this.searchService.search(params);
                this.$scope.$applyAsync();

                return searchResult.result.content;
            });
    }

    public onDirectorySelect(directory: Directory) {
        this.viewer.requestCurrentDirectoryAndSetFirstFile(directory);
    }

    public async onFileSelect(file: ViewableFile) {
        await this.viewer.requestCurrent(file);
    }

    public currentFileIndex(): number | '-' {
        if (this.viewer.current() && Array.isArray(this.viewer.files())) {
            return this.viewer.files()
                                        .map((file) => file.id)
                                        .indexOf(this.viewer.current().id) + 1;
        }

        return '-';
    }

    // ------- SEARCHING IN DIRECTORIES --------

    @ngAsync()
    public async searchInDirectories(query): Promise<number> {

        await this.searcher.search(query);

        if (!this.searcher.isEmpty()) {

            let loadedFilePromise: IPromise<any>;

            if (identifiedValuesEquals(this.viewer.current(), this.searcher.getCurrent())) {
                loadedFilePromise = this.$q.resolve();
            } else {
                loadedFilePromise = this.onFileLoaded();
            }

            // noinspection ES6MissingAwait
            loadedFilePromise.then(() => {
                if (this.mediaViewerService.current().viewForms.includes(FileViewForm.PLAIN_TEXT)) {
                    this.triggerSearchInFileByApi(this.searcher.query);
                }
            });

            this.viewer.requestCurrent(this.searcher.getCurrent());
        }

        return this.searcher.getAll().length;
    }

    @ngAsync()
    public async searchPreviousFileInDirectories(): Promise<number> {

        const fileLoadedPromise = this.onFileLoaded();

        // noinspection ES6MissingAwait
        fileLoadedPromise.then(() => {
            if (this.mediaViewerService.current().viewForms.includes(FileViewForm.PLAIN_TEXT)) {
                this.triggerSearchInFileByApi(this.searcher.query);
            }
        });

        this.viewer.requestCurrent(this.searcher.getPrevious());

        return fileLoadedPromise;
    }

    @ngAsync()
    public async searchNextFileInDirectories(): Promise<number> {

        const fileLoadedPromise = this.onFileLoaded();

        // noinspection ES6MissingAwait
        fileLoadedPromise.then(() => {
            if (this.mediaViewerService.current().viewForms.includes(FileViewForm.PLAIN_TEXT)) {
                this.triggerSearchInFileByApi(this.searcher.query);
            }
        });

        this.viewer.requestCurrent(this.searcher.getNext());

        return fileLoadedPromise;
    }

    public cancelSearchingInDirectories() {
        this.searcher.cancel();
        this.searchInDirectoriesVisible = false;
    }

    // -------- SEARCHING IN CURRENT FILE ---------

    public triggerSearchInFileByApi(query: string) {
        if (this.mediaViewerSearchInFileSearchApi) {
            this.mediaViewerSearchInFileSearchApi.cancel();
        }

        this.searchInFileVisible = true;

        this.$timeout(() => {
            this.mediaViewerSearchInFileSearchApi.search(query);
        }, 100);
    }

    @ngAsync()
    public async searchInFile(query: string): Promise<number> {
        return this.mediaViewerViewerSearchApi.search(query);
    }

    @ngAsync()
    public async searchPreviousInFile(): Promise<number> {
        return this.mediaViewerViewerSearchApi.previous();
    }

    @ngAsync()
    public async searchNextInFile(): Promise<number> {
        return this.mediaViewerViewerSearchApi.next();
    }

    public cancelSearchingInFile() {
        this.searchInFileQuery = null;
        this.mediaViewerViewerSearchApi.cancel();
        this.searchInFileVisible = false;
    }

    // ----------------------------------------------------

    public async mail(file) {
        const request: RawBodyMailSendRequest = {
            senderType: SenderDescriptorType.SYSTEM,
            recipientEmails: null,
            // @kp-localization mediaviewer.mail.Subject
            subject: this.localizationService.get('mediaviewer.mail.Subject'),
            attachments: [file.id],
            // @kp-localization mediaviewer.mail.Body
            body: this.localizationService.get('mediaviewer.mail.Body')
        };
        return this.mailerService.sendMail(request);
    }

    public print(file) {
        if (this.viewer.current().viewForms.includes(FileViewForm.PDF)) {
            // TODO: Lokalizace
            this.toasterService.showWarning('PDF soubory nezle tisknout přímo ze stránky. Stáhněte prosím tento soubor a vytiskněte pomocí programu.');
        } else {
            this.walker.newPageOnContext('/export', {target: 'files-print:/html/files-print.vtl', object: file.id}, true);
        }
    }

    public copyURLtoClipboard() {
        const temporaryTextArea = this.$document[0].createElement('textarea');
        temporaryTextArea.value = this.linkToFile;
        this.$document[0].body.appendChild(temporaryTextArea);
        temporaryTextArea.select();
        this.$document[0].execCommand('copy');
        temporaryTextArea.remove();
        this.copyLinkToClipboardVisible = false;
        // TODO: Lokalizace
        this.toasterService.showInfo('Odkaz byl zkopírován do schránky');
    }

    public changeVideoPlayerAspectRatio() {
        this.videoPlayerAspectRatio = this.videoPlayerAspectRatio === KpVideoViewerAspectRatio.A4_3 ? KpVideoViewerAspectRatio.A16_9 : KpVideoViewerAspectRatio.A4_3;
        this.$scope.$broadcast(KpVideoViewerEvent.SET_ASPECT_RATIO, this.videoPlayerAspectRatio);
    }

    private onStateChange(params: any) {
        if (params.page) {
            this.mediaViewerCurrentPage = 1;

            try {
                this.mediaViewerCurrentPage = Number.parseInt(params.page, 10);
            } finally {
                //
            }
        }
        return Promise.resolve();
    }

    private onFileLoaded(): IPromise<never> {
        return this.$q((resolve, reject) => {
            const timeoutPromise = this.$timeout(() => reject(), LOADING_FILE_TIMEOUT_MILLIS);

            const off = this.$scope.$on('mediaViewer.fileLoaded', () => {
                this.$timeout.cancel(timeoutPromise);
                resolve();
                off();
            });
        });
    }

    public get linkToFile(): string {
        const baseFileUrl = `${this.SERVER_URL}/media-viewer?rootDirectory=${this.viewer.current().directory.id}#!?file=${this.viewer.current().id}`;

        if (this.mediaViewerPageCount > 0 && this.pageNumberToLink) {
            return `${baseFileUrl}&page=${this.mediaViewerCurrentPage}`;
        }

        return baseFileUrl;
    }
}
